import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Upload, Palette } from '@phosphor-icons/react';
import BrandHeader from '../components/BrandHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Relative /api/uploads/... paths need the backend host prepended so the
// crest preview renders. Kept inline (matches every other page that uses it).
const assetUrl = (url) => (url ? (url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url) : null);

const CURRENCY_SYMBOLS = { EUR: '€', GBP: '£', USD: '$' };
const REGION_LABELS = { EUR: 'Ireland / EU', GBP: 'United Kingdom', USD: 'United States' };

const ROLES = [
  'Chairperson',
  'Treasurer',
  'Secretary',
  'Committee Member',
  'Other',
];

// Entry fee bounds: protects clubs from typos and reduces Stripe fraud-filter
// false positives on unusual amounts. Higher caps go through support.
const ENTRY_FEE_MIN = 5;
const ENTRY_FEE_MAX = 30;

/**
 * Activate page — turns a free demo into a live club site.
 *
 * Flow (top-down): tournament context → editable branding (the bits we
 * pre-filled from demo) → contact details → pricing pick → checkboxes → pay.
 *
 * Branding fields are intentionally editable here because clubs can NOT change
 * them later from the admin dashboard — the live site uses them on day one
 * for OG tags, emails, and the leaderboard header.
 */
export default function OnboardPage() {
  const [searchParams] = useSearchParams();
  const cancelled = searchParams.get('cancelled');
  // Multi-sport: when /onboard?sport=golf, swap eyebrow + headline copy AND
  // tag the resulting onboarding doc with predictor_type=golf_open_2026 so
  // the backend creates a Golf Club row (not a WC one).
  const sport = (searchParams.get('sport') || 'wc').toLowerCase();
  const isGolf = sport === 'golf';
  const isGaa = sport === 'gaa';
  const predictorType = isGaa ? 'gaa_final_2026' : isGolf ? 'golf_open_2026' : 'wc_2026';
  // Optional pricing preselect from the modal CTA (?pricing=per_entry|setup_fee).
  const pricingPreselect = searchParams.get('pricing');

  const [prices, setPrices] = useState({ EUR: 245, GBP: 195 });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [agreedLottery, setAgreedLottery] = useState(false);
  // Pricing-model picker — defaults to per_entry (the lower-friction option),
  // or whatever the modal CTA pre-selected.
  const [pricingModel, setPricingModel] = useState(
    pricingPreselect === 'setup_fee' || pricingPreselect === 'per_entry' ? pricingPreselect : 'per_entry'
  );

  // Editable branding — pre-filled from the demo URL params.
  const initialFee = Math.min(
    ENTRY_FEE_MAX,
    Math.max(ENTRY_FEE_MIN, parseFloat(searchParams.get('entry_fee')) || 10)
  );
  const [branding, setBranding] = useState({
    club_name: searchParams.get('club_name') || '',
    primary_colour: searchParams.get('primary_colour') || '#1a5632',
    secondary_colour: searchParams.get('secondary_colour') || '#ffffff',
    logo_url: searchParams.get('logo_url') || '', // existing demo logo URL
    new_logo: '',          // base64 if user replaces the crest
    new_logo_filename: '', // original filename of replacement
    entry_fee: initialFee,
  });
  const currency = searchParams.get('currency') || 'EUR';

  const [form, setForm] = useState({
    contact_name: '',
    email: searchParams.get('email') || '',
    phone: '',
    role: searchParams.get('role') || '',
  });

  useEffect(() => {
    axios.get(`${API}/onboard/config`).then(r => {
      if (r.data.prices) setPrices(r.data.prices);
    }).catch(() => {});
  }, []);

  const currentFee = prices[currency] || prices.EUR || 245;
  const currencySymbol = CURRENCY_SYMBOLS[currency] || '€';
  const regionLabel = REGION_LABELS[currency] || '';

  // What the crest preview should render: a freshly-picked file overrides the
  // saved demo URL until submission.
  const previewLogoSrc = branding.new_logo
    ? `data:image/*;base64,${branding.new_logo}`
    : assetUrl(branding.logo_url);

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError('Logo must be under 5MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1];
      setBranding(b => ({ ...b, new_logo: base64, new_logo_filename: file.name }));
    };
    reader.readAsDataURL(file);
  };

  const setBrand = (patch) => setBranding(b => ({ ...b, ...patch }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!branding.club_name.trim()) return setError('Please enter your club name.');
    if (!form.contact_name.trim()) return setError('Please enter your name.');
    if (!form.email.trim()) return setError('Please enter your email.');
    if (!form.phone.trim()) return setError('Please enter your phone number.');
    if (!form.role) return setError('Please pick your role in the club.');
    if (!agreedTerms) return setError('Please accept the Terms & Privacy to continue.');
    if (!agreedLottery) return setError('Please confirm your club has the necessary lottery licence / permissions.');

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/onboard`, {
        club_name: branding.club_name,
        primary_colour: branding.primary_colour,
        secondary_colour: branding.secondary_colour,
        currency,
        logo_url: branding.logo_url, // demo URL, kept if no replacement
        new_logo: branding.new_logo || undefined,
        new_logo_filename: branding.new_logo_filename || undefined,
        entry_fee: branding.entry_fee,
        ...form,
        lottery_licence_confirmed: agreedLottery,
        terms_accepted: agreedTerms,
        pricing_model: pricingModel,
        predictor_type: predictorType,
      });
      if (res.data.pricing_model === 'per_entry' && res.data.ready_url) {
        window.location.href = res.data.ready_url;
      } else if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        setError('Could not start activation. Please try again.');
        setSubmitting(false);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <BrandHeader />

      <main className="max-w-lg mx-auto px-6 py-12">
        {/* Hero — gives clubs the tournament context the demo had. */}
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#E63946] mb-3" data-testid="activate-eyebrow">
          {isGaa ? 'All-Ireland Final 2026 Predictor' : isGolf ? 'Open Golf 2026 Predictor' : 'World Cup 2026 Predictor'}
        </p>
        <h1 className="text-3xl font-black text-[#1D3557] mb-2" data-testid="activate-headline">
          Activate {branding.club_name || 'your club'}
        </h1>
        <p className="text-gray-600 mb-8">
          {isGaa
            ? 'Lock in your branding, choose how you want to pay, and we\u2019ll drop you straight into Stripe to connect your bank account. You go live the moment setup completes \u2014 entries close at throw-in on Sunday 26 July.'
            : isGolf
            ? 'Lock in your branding, choose how you want to pay, and we\u2019ll drop you straight into Stripe to connect your bank account. Your predictor stays private until 2nd July \u2014 plenty of time to get set up.'
            : 'Lock in your branding, choose how you want to pay, and we\u2019ll drop you straight into Stripe to connect your bank account before the tournament kicks off in June.'}
        </p>

        {cancelled && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800" data-testid="cancelled-message">
            Payment cancelled — no charge was made. You can try again below.
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8" data-testid="activate-form">

          {/* ───── 1. YOUR CLUB ─────────────────────────────────────── */}
          <section data-testid="branding-section">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#1D3557]/60 mb-3">Your club</p>

            {/* Live preview — shows colours + crest + name as a single chip
                 so clubs see exactly how the dashboard / emails / leaderboard
                 header will read before they pay. */}
            <div
              className="mb-5 p-4 rounded-lg border-2 flex items-center gap-3"
              style={{ background: branding.primary_colour, borderColor: branding.primary_colour }}
              data-testid="branding-preview"
            >
              <div
                className="shrink-0 w-14 h-14 rounded-full flex items-center justify-center"
                style={{ background: branding.secondary_colour, padding: 3 }}
              >
                <div
                  className="w-full h-full rounded-full flex items-center justify-center overflow-hidden"
                  style={{ background: branding.primary_colour }}
                >
                  {previewLogoSrc ? (
                    <img src={previewLogoSrc} alt="Crest" className="w-full h-full object-contain p-1" />
                  ) : (
                    <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: branding.secondary_colour }}>Crest</span>
                  )}
                </div>
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] truncate" style={{ color: branding.secondary_colour, opacity: 0.75 }}>
                  {isGaa ? 'All-Ireland Final 2026 Predictor' : isGolf ? 'Open Golf 2026 Predictor' : 'World Cup 2026 Predictor'}
                </p>
                <p className="text-lg font-black truncate" style={{ color: branding.secondary_colour }}>
                  {branding.club_name || 'Your Club'}
                </p>
              </div>
            </div>

            {/* Club name */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Club name</label>
              <input
                type="text"
                value={branding.club_name}
                onChange={e => setBrand({ club_name: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1D3557] focus:border-transparent"
                data-testid="branding-club-name"
                placeholder="e.g. Walterstown FC"
              />
            </div>

            {/* Colour pickers — same UX as the demo create page */}
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  <Palette size={12} weight="bold" className="inline mr-1" />
                  Primary colour
                </label>
                <div className="flex">
                  <input
                    type="color"
                    value={branding.primary_colour}
                    onChange={e => setBrand({ primary_colour: e.target.value })}
                    className="w-12 h-[42px] cursor-pointer border border-gray-300 border-r-0 rounded-l-lg"
                    data-testid="branding-primary-colour"
                  />
                  <input
                    type="text"
                    value={branding.primary_colour}
                    onChange={e => setBrand({ primary_colour: e.target.value })}
                    className="flex-1 min-w-0 px-3 py-2.5 border border-gray-300 rounded-r-lg text-sm font-mono focus:ring-2 focus:ring-[#1D3557] focus:border-transparent"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Secondary colour</label>
                <div className="flex">
                  <input
                    type="color"
                    value={branding.secondary_colour}
                    onChange={e => setBrand({ secondary_colour: e.target.value })}
                    className="w-12 h-[42px] cursor-pointer border border-gray-300 border-r-0 rounded-l-lg"
                    data-testid="branding-secondary-colour"
                  />
                  <input
                    type="text"
                    value={branding.secondary_colour}
                    onChange={e => setBrand({ secondary_colour: e.target.value })}
                    className="flex-1 min-w-0 px-3 py-2.5 border border-gray-300 rounded-r-lg text-sm font-mono focus:ring-2 focus:ring-[#1D3557] focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Crest — preview + "click to replace" mirroring the demo flow */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Club crest</label>
              <div
                className="border border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-[#1D3557]/40 transition-colors"
                onClick={() => document.getElementById('activate-logo-input').click()}
                data-testid="branding-logo-upload"
              >
                <input id="activate-logo-input" type="file" accept="image/*" className="hidden" onChange={handleLogoChange} />
                {previewLogoSrc ? (
                  <div className="flex items-center justify-center gap-4">
                    <img src={previewLogoSrc} alt="Crest preview" className="w-14 h-14 object-contain" data-testid="branding-logo-preview" />
                    <div className="text-left">
                      <p className="text-sm text-[#1D3557] font-medium">
                        {branding.new_logo_filename || 'Demo crest'}
                      </p>
                      <p className="text-xs text-gray-500">Click to replace</p>
                    </div>
                  </div>
                ) : (
                  <div className="py-2">
                    <Upload size={20} className="mx-auto text-gray-400 mb-1" />
                    <p className="text-sm text-gray-500">Click to upload your club crest</p>
                  </div>
                )}
              </div>
            </div>

            {/* Entry fee */}
            <div className="mb-2">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Entry fee per player</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-lg font-bold text-gray-500 pointer-events-none">{currencySymbol}</span>
                <input
                  type="number"
                  min={ENTRY_FEE_MIN}
                  max={ENTRY_FEE_MAX}
                  step="0.5"
                  value={branding.entry_fee}
                  onChange={(e) => {
                    const v = e.target.value === '' ? '' : parseFloat(e.target.value);
                    setBrand({ entry_fee: v });
                  }}
                  onBlur={() => {
                    const clamped = Math.min(ENTRY_FEE_MAX, Math.max(ENTRY_FEE_MIN, parseFloat(branding.entry_fee) || ENTRY_FEE_MIN));
                    if (clamped !== branding.entry_fee) setBrand({ entry_fee: clamped });
                  }}
                  className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-base font-bold focus:ring-2 focus:ring-[#1D3557] focus:border-transparent"
                  data-testid="entry-fee-input"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1.5">
                {currencySymbol}{ENTRY_FEE_MIN} minimum, {currencySymbol}{ENTRY_FEE_MAX} maximum. Need a higher entry fee?{' '}
                <a href="mailto:support@targetfunder.com" className="text-[#E63946] underline">Contact support</a>.
              </p>
            </div>

            {/* Heads-up that prizes happen later — they're sample-only on the demo. */}
            <p className="mt-4 p-3 bg-[#F8F9FC] border border-[#1D3557]/10 rounded text-xs text-gray-600" data-testid="prizes-note">
              <strong className="text-[#1D3557]">Prizes:</strong> the prizes shown on your demo are samples. You&rsquo;ll add your real prizes from your club admin dashboard once the site is live.
            </p>
          </section>

          {/* ───── 2. YOUR DETAILS ──────────────────────────────────── */}
          <section data-testid="contact-section">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#1D3557]/60 mb-3">Your details</p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Your name</label>
                <input
                  type="text"
                  required
                  value={form.contact_name}
                  onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1D3557] focus:border-transparent"
                  data-testid="input-name"
                  placeholder="e.g. Patrick Murphy"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1D3557] focus:border-transparent"
                  data-testid="input-email"
                  placeholder="you@example.com"
                />
                <p className="text-xs text-gray-500 mt-1">We&rsquo;ll send your dashboard access link here once your site is live.</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Phone</label>
                <input
                  type="tel"
                  required
                  value={form.phone}
                  onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1D3557] focus:border-transparent"
                  data-testid="input-phone"
                  placeholder="+353 87 123 4567"
                />
                <p className="text-xs text-gray-500 mt-1">Only used if we need to reach you about your setup.</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Your role in the club</label>
                <select
                  required
                  value={form.role}
                  onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1D3557] focus:border-transparent"
                  data-testid="input-role"
                >
                  <option value="">Select a role</option>
                  {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            </div>
          </section>

          {/* ───── 3. CHOOSE YOUR PRICING ───────────────────────────── */}
          <section data-testid="pricing-picker">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#1D3557]/60 mb-3">Choose your pricing</p>

            <div className="grid sm:grid-cols-2 gap-3">
              {/* Per-entry card */}
              <button
                type="button"
                onClick={() => setPricingModel('per_entry')}
                data-testid="pricing-per-entry"
                className={`text-left p-4 rounded-lg border-2 transition-all ${
                  pricingModel === 'per_entry'
                    ? 'border-[#E63946] bg-[#FFF5F5] shadow-sm'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <span className="text-base font-black uppercase tracking-wider text-[#E63946]">{currencySymbol}0 upfront</span>
                  <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${pricingModel === 'per_entry' ? 'border-[#E63946] bg-[#E63946]' : 'border-gray-300'}`}>
                    {pricingModel === 'per_entry' && <span className="block w-2 h-2 bg-white rounded-full" />}
                  </span>
                </div>
                <p className="font-bold text-[#1D3557] text-lg leading-tight mb-1">Pay per entry</p>
                {(() => {
                  // Per-entry math for this card only: {fee} × 100 − 99 cents/pence
                  // goes to the club. We floor to the nearest whole unit so
                  // the "over {N}" phrasing is honest rather than rounding up.
                  const fee = parseFloat(branding.entry_fee);
                  const hasFee = Number.isFinite(fee) && fee >= ENTRY_FEE_MIN;
                  const clubNetsMin = hasFee ? Math.floor(fee - 0.99) : null;
                  return (
                    <>
                      {hasFee && (
                        <p className="text-sm font-bold text-[#E63946] leading-snug mb-1" data-testid="per-entry-club-nets">
                          Over {currencySymbol}{clubNetsMin} from each {currencySymbol}{fee} entry goes straight to your club.
                        </p>
                      )}
                      <p className="text-xs text-gray-600 leading-relaxed">
                        <strong>{currencySymbol === '€' ? '99c' : '99p'} per entry</strong> — includes Stripe payment fees ({currencySymbol === '€' ? '49c' : '42p'}) and TargetFunder fees ({currencySymbol === '€' ? '50c' : '57p'}).
                      </p>
                    </>
                  );
                })()}
              </button>

              {/* Setup-fee card */}
              <button
                type="button"
                onClick={() => setPricingModel('setup_fee')}
                data-testid="pricing-setup-fee"
                className={`text-left p-4 rounded-lg border-2 transition-all ${
                  pricingModel === 'setup_fee'
                    ? 'border-[#1D3557] bg-[#F0F4F8] shadow-sm'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-[#1D3557]">One-off fee</span>
                  <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${pricingModel === 'setup_fee' ? 'border-[#1D3557] bg-[#1D3557]' : 'border-gray-300'}`}>
                    {pricingModel === 'setup_fee' && <span className="block w-2 h-2 bg-white rounded-full" />}
                  </span>
                </div>
                <p className="font-bold text-[#1D3557] text-lg leading-tight mb-1">Pay {currencySymbol}{currentFee} once</p>
                <p className="text-xs text-gray-600 leading-relaxed">
                  One-time setup fee. After that, you keep <strong>100% of every entry</strong> — no per-entry platform fee, ever. Stripe payment processing fee still applies ({currencySymbol === '€' ? '49c' : '42p'} per entry).
                </p>
              </button>
            </div>
          </section>

          {/* ───── 4. CHECKBOXES + SUBMIT ───────────────────────────── */}
          <div className="space-y-4">
            <label className="flex items-start gap-3 cursor-pointer p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <input
                type="checkbox"
                checked={agreedTerms}
                onChange={e => setAgreedTerms(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-[#1D3557]"
                data-testid="accept-terms"
              />
              <span className="text-sm text-gray-700">
                I agree to the <a href="https://www.targetfunder.com/terms" target="_blank" rel="noreferrer" className="text-[#1D3557] underline">Terms &amp; Privacy Policy</a>.
              </span>
            </label>

            <label className="flex items-start gap-3 cursor-pointer p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <input
                type="checkbox"
                checked={agreedLottery}
                onChange={e => setAgreedLottery(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-[#1D3557]"
                data-testid="accept-lottery"
              />
              <span className="text-sm text-gray-700">
                My club holds any licence required to run this fundraiser (e.g. Small Society Lottery licence or equivalent). <span className="text-gray-500">If you currently run a club lotto, you probably already have this.</span>
              </span>
            </label>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700" data-testid="form-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3.5 bg-[#E63946] hover:bg-[#c62d39] text-white font-bold rounded-lg transition-colors disabled:opacity-60"
              data-testid="pay-button"
            >
              {submitting
                ? 'Starting…'
                : pricingModel === 'per_entry'
                  ? 'Activate (free) → continue to Stripe'
                  : `Pay ${currencySymbol}${currentFee} setup fee`}
            </button>

            <p className="text-center text-xs text-gray-500">
              {pricingModel === 'per_entry'
                ? <>No upfront charge. {currencySymbol === '€' ? '99c' : '99p'} per entry — includes Stripe payment fees and TargetFunder fees.</>
                : <>Setup fee includes all VAT {regionLabel && `(${regionLabel})`}. You can cancel any time before payment.</>}
            </p>
          </div>
        </form>

        {/* Prep checklist so clubs know what Stripe will ask BEFORE they pay/activate */}
        <aside className="mt-8 p-5 border border-[#1D3557]/15 rounded-lg bg-[#F8F9FC]" data-testid="stripe-prep-callout">
          <p className="text-xs font-bold uppercase tracking-wider text-[#1D3557] mb-2">
            Heads up — {pricingModel === 'per_entry' ? 'after activation' : 'after payment'}
          </p>
          <p className="text-sm text-gray-700 mb-3">
            Stripe will verify your club and link its bank account (≈10&nbsp;min). Before {pricingModel === 'per_entry' ? 'activating' : 'paying'}, make sure you can put hands on:
          </p>
          <ul className="text-sm text-gray-700 space-y-1.5 pl-0 list-none">
            <li>• Club&rsquo;s <strong>bank IBAN &amp; BIC</strong> — sort code + account for UK clubs</li>
            <li>• <strong>Photo ID</strong> (passport or driving licence) of the person signing up</li>
            <li>• <strong>Club address</strong> as it appears on official correspondence</li>
            <li>• {currency === 'GBP' ? <>UK charity/club reference (if registered) or <strong>NI number</strong> for the signer</> : <><strong>PPSN</strong> or <strong>CHY number</strong> (if registered charity)</>}</li>
            <li>• A <strong>proof-of-club document</strong> — club constitution, a recent bank statement showing the club name, governing-body affiliation letter (GAA / FAI / etc.), or AGM minutes. Stripe may ask for this.</li>
          </ul>
          <p className="mt-3 text-xs text-gray-600">
            <a href="/help/stripe-setup" target="_blank" rel="noreferrer" className="text-[#1D3557] font-semibold underline" data-testid="stripe-help-link">
              Full step-by-step guide →
            </a>
          </p>
        </aside>

        <div className="mt-10 pt-6 border-t border-gray-200 text-xs text-gray-500 space-y-1">
          <p>Operated by TargetFunder — part of Castleview Resources Ltd, Dublin 16, Ireland.</p>
          <p>Need help? <a className="underline" href="mailto:support@targetfunder.com">support@targetfunder.com</a></p>
        </div>
      </main>
    </div>
  );
}
